import { z } from 'zod';
import { PasswordLinkResponseSchema } from './auth.js';
import { IsoDateTimeSchema, PhoneInputSchema, UuidSchema } from './common.js';
import { RoleSchema } from './tenancy.js';

/**
 * The platform panel (ADR-045): the clinics, their staff and counts. Never a patient, a visit or an
 * amount of money of a clinic.
 */
export const PlatformClinicSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  slug: z.string(),
  status: z.enum(['active', 'suspended']),
  laserEnabled: z.boolean(),
  createdAt: IsoDateTimeSchema,
  /** Active members. */
  members: z.number().int(),
  adminName: z.string().nullable(),
  adminPhone: z.string().nullable(),
  /** Counts only. */
  patients: z.number().int(),
  appointments30d: z.number().int(),
  visits30d: z.number().int(),
  lastActivityAt: IsoDateTimeSchema.nullable(),
});
export type PlatformClinic = z.infer<typeof PlatformClinicSchema>;
export const PlatformClinicListSchema = z.object({ items: z.array(PlatformClinicSchema) });

export const PlatformMemberSchema = z.object({
  membershipId: UuidSchema,
  userId: UuidSchema,
  displayName: z.string().nullable(),
  phone: z.string(),
  roles: z.array(RoleSchema),
  status: z.enum(['active', 'removed']),
  createdAt: IsoDateTimeSchema,
  hasPassword: z.boolean(),
  /** Belongs to this clinic alone: a password link may be made for them. */
  exclusive: z.boolean(),
});
export type PlatformMember = z.infer<typeof PlatformMemberSchema>;
export const PlatformMemberListSchema = z.object({ items: z.array(PlatformMemberSchema) });

export const PlatformClinicCreateInputSchema = z.object({
  name: z.string().trim().min(2, { message: 'نام کلینیک دست‌کم ۲ نویسه است.' }).max(200, { message: 'نام کلینیک حداکثر ۲۰۰ نویسه است.' }),
  adminPhone: PhoneInputSchema,
  adminName: z
    .string()
    .trim()
    .max(100)
    .nullish()
    .transform((v) => (v ? v : null)),
});
export type PlatformClinicCreateInput = z.input<typeof PlatformClinicCreateInputSchema>;

export const PlatformClinicCreateResultSchema = z.object({
  clinic: PlatformClinicSchema,
  /** For the first admin; null when the account belongs to other organizations too. */
  passwordLink: PasswordLinkResponseSchema.nullable(),
});
export type PlatformClinicCreateResult = z.infer<typeof PlatformClinicCreateResultSchema>;

export const PlatformClinicUpdateInputSchema = z.object({
  name: PlatformClinicCreateInputSchema.shape.name,
  laserEnabled: z.boolean(),
});
export type PlatformClinicUpdateInput = z.input<typeof PlatformClinicUpdateInputSchema>;

/** Deleting a suspended clinic for good (ADR-046): its name, typed exactly as it is written. */
export const PlatformClinicDeleteInputSchema = z.object({
  name: z.string().min(1, { message: 'نام کلینیک را بنویسید.' }).max(200),
});
export type PlatformClinicDeleteInput = z.input<typeof PlatformClinicDeleteInputSchema>;
export const PlatformClinicDeleteResultSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  /** Rows removed, per table (counts only). */
  tables: z.record(z.string(), z.number().int()),
});
export type PlatformClinicDeleteResult = z.infer<typeof PlatformClinicDeleteResultSchema>;

/**
 * The system status of the platform panel (docs/pilot-ops/design.md §2.4): the scheduled jobs, the
 * latest nightly reconciliation this server has seen, its server errors of the last hour and its
 * build. Names and counts only.
 */
export const OpsJobSchema = z.object({
  name: z.string(),
  lastBeatAt: IsoDateTimeSchema.nullable(),
  restarts: z.number().int(),
  lastPanicAt: IsoDateTimeSchema.nullable(),
  /** No beat for three of its intervals. */
  stale: z.boolean(),
});
export type OpsJob = z.infer<typeof OpsJobSchema>;
export const OpsStatusSchema = z.object({
  status: z.enum(['ok', 'attention']),
  version: z.string(),
  startedAt: IsoDateTimeSchema,
  jobs: z.array(OpsJobSchema),
  reconciliation: z.object({ day: z.string(), ok: z.boolean() }).nullable(),
  serverErrorsLastHour: z.number().int(),
  /** Failures the browsers reported in the last hour (shown, never alerted on: extensions and old phones are noisy). */
  clientErrorsLastHour: z.number().int(),
  /** The caller's address as the server sees it (the panel only). */
  clientIp: z.string().optional(),
});
export type OpsStatus = z.infer<typeof OpsStatusSchema>;
